import React, {ChangeEvent, Fragment, useEffect, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {
  CForm,
  CFormCheck,
  CFormLabel,
  CTabContent,
  CTable, CTableBody, CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow, CTabPane
} from "@coreui/react";
import Select from "react-select";
import {useGetEmergencyTypesQuery} from "../../../store/emergencyTypes/emergencyTypes.api";
import makeQuery from "../../../functions/makeQuery";
import {MultilanguageStringParse} from "../../../functions/MultilanguageStringParse";
import {Numb, sortArrayOfObjectsByKey} from "../../../functions";
import LoaderLine from "../../../components/preloaders/LoaderLine/LoaderLine";
import {useLazyGetComparativeReportQuery} from "../../../store/analytics/analytics.api";
import LineChart from "./Charts/LineChart";
import BarChart from "./Charts/BarChart";
import RadarChart from "./Charts/RadarChart";

const yearsList = new Array<number>(new Date().getFullYear() - 2014).fill(0).map((_, i) => i + 2015);

const reactSelectControlStyles = (baseStyles: any, state: any): any => {
  return {
    ...baseStyles,
    '&:hover': {
      borderColor: state.isFocused ? '#998fed' : '#d8dbe0'
    },
    borderColor: state.isFocused ? '#998fed' : '#d8dbe0',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(50, 31, 219, 0.25)' : 'unset',
    marginBottom: '5px'
  }
};

type TReportConfig = {
  esType: {label: string; value: number}[],
  yearFrom: number,
  yearTo: number,
  materialDamage: boolean,
  humanCasualties: boolean,
  numberOfVictims: boolean
};

type TReportTable = {
  id: number,
  type: string,
  years: {year: number, total: number, humanCasualties: number, numberOfVictims: number, materialDamage: number}[],
};

type TLineChartDataset = {
  label: string,
  data: number[]
};

const ComparativeReport = () => {
  const { t, i18n } = useTranslation();

  const [reportConfig, setReportConfig] = useState<TReportConfig>({
    esType: [],
    yearFrom: yearsList[yearsList.length - 2],
    yearTo: yearsList[yearsList.length - 1],
    humanCasualties: true,
    numberOfVictims: true,
    materialDamage: true
  });
  const [reportTable, setReportTable] = useState<TReportTable[]>([]);
  const [lineChartDataset, setLineChartDataset] = useState<TLineChartDataset[]>([]);
  const [activeKey, setActiveKey] = useState<number>(1);
  const [currentChartType, setCurrentChartType] = useState<{label: string, value: string}>({label: t('analytics.LineChart'), value: 'LineChart'});

  const {data: esTypes, isLoading: esTypesLoading} = useGetEmergencyTypesQuery(makeQuery.stringify({page_size: 100000, sort_by: 'name', sort_direction: 'asc'}));
  const [getComparativeReport, {data: reportData, isLoading: reportDataLoading, isFetching: reportDataFetching}] = useLazyGetComparativeReportQuery();

  useEffect(() => {
    const queryData = {
      date_emergency__gte: yearsList[0] + '-01-01',
      date_emergency__lte: yearsList[yearsList.length - 1] + '-12-31'
    };
    getComparativeReport(makeQuery.stringify(queryData));
  }, []);

  useEffect(() => {
    try {
      const list = reportData?.map(type => ({
        id: type.type_emergency_id,
        type: MultilanguageStringParse(type.type_emergency, i18n.language),
        years: new Array(reportConfig.yearTo - reportConfig.yearFrom + 1).fill(0).map((_, i) => {
          const year = reportConfig.yearFrom + i;
          const res = {
            year: year,
            total: 0,
            humanCasualties: 0,
            numberOfVictims: 0,
            materialDamage: 0
          };
          const yearData = type.years.find(y => y.year === year);
          if (yearData) {
            res.total = yearData.total_type_emergency;
            res.humanCasualties = yearData.total_human_casualties;
            res.numberOfVictims = yearData.total_number_of_victims;
            res.materialDamage = yearData.total_material_damage;
          }
          return res;
        }),
      }));
      setReportTable(sortArrayOfObjectsByKey(list || [], 'type'));
    } catch (e) {
      // do nothing
    }
  }, [reportData, reportConfig]);

  const isLoading = useMemo(() => {
    return esTypesLoading || reportDataLoading || reportDataFetching;
  }, [esTypesLoading, reportDataLoading, reportDataFetching]);

  const esTypesOptions = useMemo(() => {
    try {
      const list = esTypes?.results?.map(el => (
        { label: MultilanguageStringParse(el.name, i18n.language), value: el.id }
      ));
      const res = sortArrayOfObjectsByKey(list || [], 'label');
      res.unshift({ label: t('analytics.Select all'), value: 0 });
      return res;
    } catch (e) {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esTypes]);

  const chartTypesOptions = useMemo(() => {
    return [
      { label: t('analytics.LineChart'), value: "LineChart" },
      { label: t('analytics.BarChart'), value: "BarChart" },
      { label: t('analytics.RadarChart'), value: "RadarChart" }
    ]
  }, [i18n.language]);

  const onChangeHandler = (value: any, name: string) => {
    const innerState: TReportConfig = {...reportConfig};

    switch (name) {
      case 'esType':
        if (value.find((el: any) => el.value === 0)) {
          innerState.esType = [];
          if (value.length !== esTypesOptions.length) {
            esTypesOptions.forEach((el: any) => {
              if (el.value) innerState.esType.push(el);
            });
          }
        } else {
          innerState.esType = value;
        }
        break;
      case 'yearFrom':
        innerState.yearFrom = value.value;
        if (innerState.yearFrom > innerState.yearTo) innerState.yearTo = value.value;
        break;
      case 'yearTo':
        innerState.yearTo = value.value;
        if (innerState.yearFrom > innerState.yearTo) innerState.yearFrom = value.value;
        break;
      default:
      // do nothing
    }

    setReportConfig(innerState);
  };

  const onCheckHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, checked} = e.target;
    if (name === 'materialDamage' || name === 'humanCasualties' || name === 'numberOfVictims') {
      const innerState: TReportConfig = {...reportConfig};
      innerState[name] = checked;
      setReportConfig(innerState);
    }
  };

  useEffect(() => {
    const lineDataset = [...reportTable].filter(el => !!reportConfig.esType?.find(type => type.value === el.id)).map(el => ({
      label: el.type,
      data: el.years.map(year => year.total)
    }));
    setLineChartDataset(lineDataset);
  }, [reportConfig, reportTable]);

  const onChangeChartTypeHandler = (value: any) => {
    setCurrentChartType(value);
  };

  const renderReportTable = useMemo(() => {
    const colsCount = (reportTable[0]?.years.length || 1) + 1;
    let yearWidth = 70 / colsCount;
    let typeWidth = 15;
    if (colsCount < 6) {
      yearWidth = 50 / colsCount;
      typeWidth = 25;
    } else if (colsCount < 9) {
      yearWidth = 60 / colsCount;
      typeWidth = 20;
    }
    const rowsCount = 1 + (reportConfig.humanCasualties ? 1 : 0) + (reportConfig.numberOfVictims ? 1 : 0) + (reportConfig.materialDamage ? 1 : 0);

    return (
      <CTable bordered striped className="default-table mb-4">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" colSpan={2}/>
            {reportTable[0]?.years.map(year => (
              <CTableHeaderCell key={'yearHead' + year.year}
                                scope="col"
                                className="text-center"
                                style={{width: yearWidth + '%'}}
              >
                {year.year}
              </CTableHeaderCell>
            ))}
            <CTableHeaderCell scope="col" className="text-center" style={{width: yearWidth + '%'}}>
              За период
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>

        <CTableBody>
          {[...reportTable].filter(el => !!reportConfig.esType?.find(type => type.value === el.id)).map((type, typeIndex) => {
            let total = 0, casualties = 0, victims = 0, damage = 0;
            return (
              <Fragment key={'typeRow' + typeIndex}>
                <CTableRow>
                  <CTableDataCell rowSpan={rowsCount} style={{fontWeight: 'bold', verticalAlign: 'middle'}}>
                    {type.type}
                  </CTableDataCell>
                  <CTableDataCell style={{width: typeWidth + '%'}}>
                    Количество происшествий
                  </CTableDataCell>
                  {type.years.map(year => {
                    total += year.total;
                    return (
                      <CTableDataCell key={typeIndex + 'total' + year.year} className="text-center">
                        {Numb.split(year.total)}
                      </CTableDataCell>
                    );
                  })}
                  <CTableDataCell className="text-center">
                    {Numb.split(total)}
                  </CTableDataCell>
                </CTableRow>

                {reportConfig.humanCasualties ?
                  <CTableRow>
                    <CTableDataCell>
                      Количество жертв
                    </CTableDataCell>
                    {type.years.map(year => {
                      casualties += year.humanCasualties;
                      return (
                        <CTableDataCell key={typeIndex + 'casualties' + year.year} className="text-center">
                          {Numb.split(year.humanCasualties)}
                        </CTableDataCell>
                      );
                    })}
                    <CTableDataCell className="text-center">
                      {Numb.split(casualties)}
                    </CTableDataCell>
                  </CTableRow>
                  : null}

                {reportConfig.numberOfVictims ?
                  <CTableRow>
                    <CTableDataCell>
                      Количество пострадавших
                    </CTableDataCell>
                    {type.years.map(year => {
                      victims += year.numberOfVictims;
                      return (
                        <CTableDataCell key={typeIndex + 'victims' + year.year} className="text-center">
                          {Numb.split(year.numberOfVictims)}
                        </CTableDataCell>
                      );
                    })}
                    <CTableDataCell className="text-center">
                      {Numb.split(victims)}
                    </CTableDataCell>
                  </CTableRow>
                  : null}

                {reportConfig.materialDamage ?
                  <CTableRow>
                    <CTableDataCell>
                      Материальный ущерб
                    </CTableDataCell>
                    {type.years.map(year => {
                      damage += year.materialDamage;
                      return (
                        <CTableDataCell key={typeIndex + 'damage' + year.year} className="text-center">
                          {Numb.split(year.materialDamage)}
                        </CTableDataCell>
                      );
                    })}
                    <CTableDataCell className="text-center">
                      {Numb.split(damage)}
                    </CTableDataCell>
                  </CTableRow>
                  : null}
              </Fragment>
            );
          })}
        </CTableBody>
      </CTable>
    );
  }, [reportConfig, reportTable]);

  return (
    <>
      <div className="pb-3 mb-4 title-divider">
        <h1>{t('nav.Comparative report')}</h1>
        <LoaderLine visible={isLoading}/>
      </div>

      <CForm>
        <div className={'mb-3'}>
          <CFormLabel>
            {t("analytics.es_type")}
            <span style={{color: 'red'}}>&nbsp;*</span>
          </CFormLabel>
          <Select options={esTypesOptions || []}
                  value={reportConfig.esType}
                  placeholder={t('Nothing is selected')}
                  styles={{control: (baseStyles, state) => (reactSelectControlStyles(baseStyles, state))}}
                  onChange={e => onChangeHandler(e, 'esType')}
                  isMulti
                  closeMenuOnSelect={false}
          />
        </div>

        <div className={'mb-3'}>
          <CFormLabel>
            {t("esForm.period")}
            <span style={{color: 'red'}}>&nbsp;*</span>
          </CFormLabel>
          <div className={'analytics-form-row'}>
            <Select options={[...yearsList].reverse().map(year => ({label: year, value: year})) || []}
                    value={{label: reportConfig.yearFrom, value: reportConfig.yearFrom}}
                    styles={{control: (baseStyles, state) => (reactSelectControlStyles(baseStyles, state))}}
                    onChange={e => onChangeHandler(e, 'yearFrom')}
            />
            <Select options={[...yearsList].reverse().map(year => ({label: year, value: year})) || []}
                    value={{label: reportConfig.yearTo, value: reportConfig.yearTo}}
                    styles={{control: (baseStyles, state) => (reactSelectControlStyles(baseStyles, state))}}
                    onChange={e => onChangeHandler(e, 'yearTo')}
            />
          </div>
        </div>

        <div className={'mb-3'}>
          <CFormLabel>
            {t("templates.settings")}
          </CFormLabel>
          <div className={'analytics-form-row'}>
            <CFormCheck type="checkbox"
                        name="humanCasualties"
                        id="humanCasualties"
                        label={t("analytics.human_casualties")}
                        checked={reportConfig.humanCasualties}
                        onChange={onCheckHandler}
            />
            <CFormCheck type="checkbox"
                        name="numberOfVictims"
                        id="numberOfVictims"
                        label={t("analytics.number_of_victims")}
                        checked={reportConfig.numberOfVictims}
                        onChange={onCheckHandler}
            />
            <CFormCheck type="checkbox"
                        name="materialDamage"
                        id="materialDamage"
                        label={t("analytics.material_damage")}
                        checked={reportConfig.materialDamage}
                        onChange={onCheckHandler}
            />
          </div>
        </div>
      </CForm>

      {reportConfig.esType.length ?
        <>
          <div style={{display: 'flex', justifyContent: 'flex-start'}}>
            <div className={activeKey === 1 ? 'active-tab' : 'no-active-tab'}
                 style={{display: 'flex', justifyContent: 'center'}} onClick={() => setActiveKey(1)}>
              {t("analytics.dataTable")}
            </div>
            <div className={activeKey === 2 ? 'active-tab' : 'no-active-tab'}
                 style={{display: 'flex', justifyContent: 'center'}} onClick={() => setActiveKey(2)}>
              {t("analytics.visualization")}
            </div>
          </div>
          <CTabContent>
            <CTabPane role="tabpanel" aria-labelledby="table-tab" visible={activeKey === 1}
                      style={{padding: '15px', background: "white"}}>
              {renderReportTable}
            </CTabPane>
            <CTabPane role="tabpanel" aria-labelledby="chart-tab" visible={activeKey === 2}
                      style={{margin: '0 1px 0 1px', padding: '15px', background: "white"}}>

              <div className="analytics-form-row mt-3">
                <div style={{minWidth: '250px'}}>
                  <Select options={chartTypesOptions}
                          value={currentChartType}
                          styles={{control: (baseStyles, state) => (reactSelectControlStyles(baseStyles, state))}}
                          onChange={onChangeChartTypeHandler}
                  />
                </div>
              </div>

              {currentChartType.value === 'LineChart' ?
                <div style={{overflow: 'hidden', padding: '20px 100px'}}>
                  <LineChart labels={reportTable[0]?.years.map(year => String(year.year)) || []}
                             data={lineChartDataset}
                  />
                </div>
                : null}
              {currentChartType.value === 'BarChart' ?
                <div style={{overflow: 'hidden', padding: '20px 100px'}}>
                  <BarChart labels={reportTable[0]?.years.map(year => String(year.year)) || []}
                            data={lineChartDataset}
                  />
                </div>
                : null}
              {currentChartType.value === 'RadarChart' ?
                <div style={{overflow: 'hidden', padding: '20px 15%'}}>
                  <RadarChart labels={reportTable[0]?.years.map(year => String(year.year)) || []}
                              data={lineChartDataset}
                  />
                </div>
                : null}

            </CTabPane>
          </CTabContent>
        </>
        : null}

      <div style={{paddingTop: '100px'}}></div>
    </>
  );
};

export default ComparativeReport;
